import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { getDashboard } from "../../services/dashboardService";
import { getReceivables } from "../../services/orgService";
import { rateFreshness } from "../../services/catalogueService";

// What needs someone today.
//
// Tiles are jobs, not statistics: a number that is zero is not shown, because
// a screen full of zeroes teaches you to stop reading it. Our own database
// paints at once; the two figures that live in Zoho Books and the CRM arrive
// after, so a slow call to someone else's API never holds up the view of our
// own work - and if one of them fails the rest of the page is still useful.

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const Tile = ({ to, label, value, note, tone = "dark" }) => (
  <div className="col-6 col-md-4 col-xl-3">
    <Link to={to} className="text-decoration-none">
      <div className="card h-100">
        <div className="card-body py-3">
          <div className={`fs-4 fw-bold text-${tone}`}>{value}</div>
          <div className="small fw-semibold text-dark">{label}</div>
          {note && <div className="small text-muted">{note}</div>}
        </div>
      </div>
    </Link>
  </div>
);

const Dashboard = () => {
  const [d, setD] = useState(null);
  const [money, setMoney] = useState(null);
  const [costs, setCosts] = useState(null);

  const load = useCallback(async () => {
    try { setD(await getDashboard()); } catch (e) { setD({ error: true }); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // the slow two, separately and each allowed to fail on its own
  useEffect(() => { getReceivables().then(setMoney).catch(() => setMoney({ error: true })); }, []);
  useEffect(() => { rateFreshness({ staleDays: 90 }).then(setCosts).catch(() => setCosts({ error: true })); }, []);

  if (!d) return null;
  if (d.error) return <div className="page-body ms-3 mt-3 text-muted">Could not load the dashboard.</div>;

  const w = d.work;
  const tiles = [];
  if (w.needsPricing) tiles.push(<Tile key="np" to="/requirements?status=Received,Matching&open=1" label="Requirements to price" value={w.needsPricing} note="matched or waiting to be" tone="warning" />);
  if (w.awaitingRates) tiles.push(<Tile key="ar" to="/requirements?status=Awaiting%20Rates&open=1" label="Waiting on a vendor rate" value={w.awaitingRates} tone="warning" />);
  if (w.quotedAwaitingDecision) tiles.push(<Tile key="qd" to="/requirements?customerStatus=Quoted" label="Quoted, awaiting the customer" value={w.quotedAwaitingDecision} />);
  if (w.revisionRequested) tiles.push(<Tile key="rr" to="/requirements?customerStatus=Revision%20Requested" label="Revision asked for" value={w.revisionRequested} tone="danger" />);
  if (w.ratesToConfirm) tiles.push(<Tile key="rc" to="/vendor-inbox" label="Vendor rates to confirm" value={w.ratesToConfirm} note="a photo never writes on its own" tone="warning" />);
  if (w.vendorRepliesUnread) tiles.push(<Tile key="vr" to="/vendor-inbox" label="Vendor replies this week" value={w.vendorRepliesUnread} />);
  if (w.questionsQueued) tiles.push(<Tile key="qq" to="/vendor-inbox" label="Questions queued to send" value={w.questionsQueued} />);
  if (w.ordersAwaitingPo) tiles.push(<Tile key="po" to="/orders" label="Orders awaiting a PO" value={w.ordersAwaitingPo} tone="warning" />);
  if (w.ordersOpen) tiles.push(<Tile key="oo" to="/orders" label="Orders in progress" value={w.ordersOpen} />);
  if (w.accessRequests) tiles.push(<Tile key="acc" to="/organizations" label="Societies asking for access" value={w.accessRequests} tone="success" />);

  if (money && !money.error && money.connected && money.total > 0) {
    const worst = money.rows[0];
    tiles.push(<Tile key="money" to="/receivables" label="Pending collection" value={inr(money.total)} note={worst ? `oldest ${worst.oldestDays} days — ${worst.customer}` : null} tone={worst && worst.oldestDays > 90 ? "danger" : "dark"} />);
  }
  if (costs && !costs.error && costs.stale > 0) {
    tiles.push(<Tile key="cost" to="/catalogue/rate-freshness" label="Vendor costs over 90 days old" value={costs.stale} note={costs.liveAtRisk ? `${costs.liveAtRisk} under a live price` : "none under a live price"} tone={costs.liveAtRisk ? "danger" : "warning"} />);
  }
  if (d.catalogue.draft) tiles.push(<Tile key="cat" to="/catalogue/price-list" label="Listings not live yet" value={d.catalogue.draft} note="need a price, then approval" tone="warning" />);

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2">
          <h6 className="fw-bold mb-0">Today</h6>
          <div className="small text-muted">
            {d.catalogue.approved} of {d.catalogue.listings} listings live · {d.activity.organizations} society on the portal · {d.activity.newRequirementsThisWeek} new requirement(s) this week
          </div>
        </div>

        <div className="row g-2 mt-1">
          {tiles.length ? tiles : <div className="col-12 text-muted px-2">Nothing waiting. Everything priced, quoted and answered.</div>}
        </div>

        <div className="row g-3 mt-2">
          {d.stuck.length > 0 && (
            <div className="col-lg-6">
              <div className="card h-100"><div className="card-body">
                <h6 className="fw-bold">Oldest unfinished requirements</h6>
                {d.stuck.map((r) => (
                  <div key={r.number} className="d-flex justify-content-between border-bottom py-2">
                    <div>
                      <Link to={`/requirements/${r.number}`}>{r.number}</Link>
                      <span className="text-muted small"> · {r.account}</span>
                    </div>
                    <div className="small">
                      <span className="badge bg-light text-dark me-2">{r.status}</span>
                      <span className={r.waitingDays > 7 ? "text-danger" : "text-muted"}>{r.waitingDays} d</span>
                    </div>
                  </div>
                ))}
              </div></div>
            </div>
          )}

          {d.openRequisitions.length > 0 && (
            <div className="col-lg-6">
              <div className="card h-100"><div className="card-body">
                <h6 className="fw-bold">Asks out with vendors</h6>
                {d.openRequisitions.map((r) => (
                  <div key={r.id} className="d-flex justify-content-between border-bottom py-2">
                    <div>{r.vendor}</div>
                    <div className="small">
                      <span className="badge bg-light text-dark me-2">{r.status}</span>
                      {r.waitingDays !== null && <span className={r.waitingDays > 3 ? "text-danger" : "text-muted"}>{r.waitingDays} d</span>}
                    </div>
                  </div>
                ))}
              </div></div>
            </div>
          )}
        </div>

        {money && money.error && <div className="small text-muted mt-3 px-2">Collection figures could not be read from Zoho Books just now.</div>}
      </div>
    </div>
  );
};

export default Dashboard;
